import { useLazyQuery } from '@apollo/client';

import admanToast from '../../../../components/Alert/util/admanToast';
import { isDemoEnvironment } from '../../../../constants/global';
import { downloadFile } from '../../../../utils/download';
import { Dimensions, DownloadPerformanceReportsQuery, DownloadPerformanceReportsQueryVariables } from '../../types';
import { DOWNLOAD_PERFORMANCE_REPORTS } from './downloadPerformanceReports';

export type UseDownloadPerformanceReportProps = {
  lineItems: string[];
  campaignId: string;
};

export type UseDownloadPerformanceReport = {
  loading: boolean;
  getAllPerformance: () => Promise<void>;
  getTotalImpressions: () => Promise<void>;
  getAudiences: () => Promise<void>;
  getPlatforms: () => Promise<void>;
  getGenres: () => Promise<void>;
  getLocations: () => Promise<void>;
};

const useDownloadPerformanceReport = ({
  lineItems,
  campaignId,
}: UseDownloadPerformanceReportProps): UseDownloadPerformanceReport => {
  const [downloadPerformance, { loading }] = useLazyQuery<
    DownloadPerformanceReportsQuery,
    DownloadPerformanceReportsQueryVariables
  >(DOWNLOAD_PERFORMANCE_REPORTS, {
    fetchPolicy: 'network-only',
  });

  const handleDownloadFile = async (
    variables: DownloadPerformanceReportsQueryVariables,
    fileName?: string
  ): Promise<void> => {
    if (isDemoEnvironment) {
      const response = await fetch(`${process.env.PUBLIC_URL}/report.zip`);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'report.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return;
    }

    const { data, error } = await downloadPerformance({ variables });
    const link = data?.downloadPerformanceReports;

    if (error) {
      admanToast.error('Can not get report download link');
      return;
    }

    if (!link) return;

    await downloadFile(
      link,
      () => {
        admanToast.error('Can not download performance report. Please try again later.');
      },
      fileName || link.split('?')[0]
    );
  };

  const getAllPerformance = async (): Promise<void> => {
    await handleDownloadFile(
      {
        campaignId,
        lineItems,
        dimensionList: [null, Dimensions.Locations, Dimensions.Audience, Dimensions.Platform, Dimensions.Genre],
      },
      'All Performance Reports'
    );
  };

  const getTotalImpressions = async (): Promise<void> => {
    await handleDownloadFile(
      {
        lineItems,
        campaignId,
        dimensionList: [null],
      },
      'Total Impressions'
    );
  };

  const getAudiences = async (): Promise<void> => {
    await handleDownloadFile(
      {
        lineItems,
        campaignId,
        dimensionList: [Dimensions.Audience],
      },
      'Audiences'
    );
  };

  const getPlatforms = async (): Promise<void> => {
    await handleDownloadFile(
      {
        lineItems,
        campaignId,
        dimensionList: [Dimensions.Platform],
      },
      'Platforms'
    );
  };

  const getGenres = async (): Promise<void> => {
    await handleDownloadFile(
      {
        lineItems,
        campaignId,
        dimensionList: [Dimensions.Genre],
      },
      'Genres'
    );
  };

  const getLocations = async (): Promise<void> => {
    await handleDownloadFile(
      {
        lineItems,
        campaignId,
        dimensionList: [Dimensions.Locations],
      },
      'Locations'
    );
  };

  return {
    loading,
    getAllPerformance,
    getTotalImpressions,
    getAudiences,
    getPlatforms,
    getGenres,
    getLocations,
  };
};

export default useDownloadPerformanceReport;
